import React, { createContext, useCallback, useContext } from "react";
import {
  persistSubscription,
  requestNotificationPermissionAndSubscribe,
  setupServiceWorkerMessageListener,
} from "../helper/pushNotifications";
import { useSession } from "../stores/sessionStore";

interface PushNotificationContextType {
  /**
   * Request notification permission and subscribe for push notifications.
   * Call this after order placement to register for order updates.
   * @param userId - The customerId from the order response (required for guest checkout)
   * @returns Promise<boolean> - true if subscription was successful
   */
  subscribeForOrderUpdates: (userId: string) => Promise<boolean>;
}

const PushNotificationContext = createContext<PushNotificationContextType>({
  subscribeForOrderUpdates: async () => false,
});

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error(
      "usePushNotifications must be used within a PushNotificationProvider",
    );
  }
  return context;
};

interface PushNotificationProviderProps {
  children: React.ReactNode;
}

export const PushNotificationProvider: React.FC<
  PushNotificationProviderProps
> = ({ children }) => {
  const { restaurantId } = useSession();

  const subscribeForOrderUpdates = useCallback(
    async (userId: string): Promise<boolean> => {
      try {
        // Check if push notifications are supported
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
          console.warn("Push notifications not supported in this browser");
          return false;
        }

        if (!userId) {
          console.error("Cannot subscribe: userId (customerId) is required");
          return false;
        }

        if (!restaurantId) {
          console.error("Cannot subscribe: restaurantId is missing");
          return false;
        }

        // Setup the message listener with userId and restaurantId
        setupServiceWorkerMessageListener(userId, restaurantId);

        // Request permission and get subscription
        const subscription = await requestNotificationPermissionAndSubscribe();

        if (!subscription) {
          console.warn(
            "Failed to get push subscription (permission denied or error)",
          );
          return false;
        }

        // Persist to backend
        const success = await persistSubscription(userId, subscription);

        if (success) {
          console.log("Successfully subscribed for order updates");
        } else {
          console.warn("Failed to persist push subscription to backend");
        }

        return success;
      } catch (error) {
        console.error("Error subscribing for order updates:", error);
        return false;
      }
    },
    [restaurantId],
  );

  return (
    <PushNotificationContext.Provider value={{ subscribeForOrderUpdates }}>
      {children}
    </PushNotificationContext.Provider>
  );
};

export default PushNotificationProvider;
